import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Typography,
} from '@mui/material';
import { CreateNewFolder as FolderIcon } from '@mui/icons-material';

const CreateFolderModal = ({ 
  open, 
  onClose, 
  onSubmit, 
  parentFolderID, 
  currentDepth,
  maxDepth,
  clientID 
}) => {
  const [folderName, setFolderName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  
  const handleSubmit = () => {
    // Validation
    if (!folderName.trim()) {
      setError('Folder name is required');
      return;
    }
    
    if (folderName.length > 100) {
      setError('Folder name must be less than 100 characters');
      return;
    }
    
    if (currentDepth >= maxDepth) {
      setError(`Maximum folder depth of ${maxDepth} levels reached`);
      return;
    }
    
    // Submit the folder data
    onSubmit({
      name: folderName.trim(),
      description: description.trim(),
      parentFolderID,
      clientID,
      depth: currentDepth,
    });
    
    // Reset form
    handleClose();
  };
  
  const handleClose = () => {
    setFolderName('');
    setDescription('');
    setError('');
    onClose();
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FolderIcon sx={{ mr: 1 }} />
          Create New Folder
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {currentDepth >= maxDepth - 1 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are creating a folder at depth {currentDepth + 1} of {maxDepth}. 
            This folder cannot have subfolders.
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <TextField
          autoFocus
          margin="dense"
          label="Folder Name"
          fullWidth
          variant="outlined"
          value={folderName}
          onChange={(e) => {
            setFolderName(e.target.value);
            setError('');
          }}
          required
          helperText={`${folderName.length}/100 characters`}
          sx={{ mb: 2 }}
        />
        
        <TextField
          margin="dense"
          label="Description (Optional)"
          fullWidth
          variant="outlined"
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          helperText="Brief description of what this folder contains"
        />
        
        {parentFolderID && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            This folder will be created as a subfolder at depth {currentDepth + 1}
          </Typography>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={!folderName.trim()}
        >
          Create Folder
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateFolderModal;